import React, { useState } from "react";
import { Chart } from "react-chartjs-2";
import { Chart as ChartJS, Tooltip } from "chart.js";
import { TreemapController, TreemapElement } from "chartjs-chart-treemap";
import FinanceCardChart from "../../finance/financeCardChart/financeCardChart";
import ZeroData from "../../zeroData/zeroData";
import InfoIcon from "../../../assets/icons/info-icon.svg";

ChartJS.register(TreemapController, TreemapElement, Tooltip);

// Tons da marca, do maior (mais escuro) ao menor. ponytail: paleta fixa, sem escala contínua.
const PALETTE = ["#0962AD", "#2C79BE", "#4F91CF", "#72A9E0", "#95C1EC", "#B8D6F4"];

/**
 * Treemap genérico "valor por X".
 * items: array com { [labelKey], [valueKey] }. Aplica Top-N + "Outros".
 * formatValue: formata o valor no bloco e no tooltip (default: inteiro + "alunos").
 * restAggregate: "sum" (default) ou "avg" — use "avg" quando o valor for uma média (ex.: ticket médio).
 */
const StudentsTreemap = ({
  title,
  code,
  info,
  items = [],
  labelKey,
  valueKey,
  filterField,
  onSelect,
  topN = 15,
  formatValue,
  restAggregate = "sum",
  height
}) => {
  const [showPopover, setShowPopover] = useState(false);

  // Clicar num bloco carrega a tabela filtrada abaixo (via onSelect no pai).
  // "Outros" agrega tudo fora do Top-N, então filtra por todos esses valores (operador IN).
  const selectBlock = (label) => {
    if (!filterField || !onSelect || !label) {
      return;
    }
    if (label === "Outros") {
      const values = rest.map((r) => String(r.label));
      if (!values.length) {
        return;
      }
      onSelect({ field: filterField, operator: "in", values, display: "Outros", sourceTitle: title });
      return;
    }
    onSelect({ field: filterField, operator: "eq", values: [String(label)], display: String(label), sourceTitle: title });
  };

  const sorted = (items || [])
    .map((i) => ({ label: String(i[labelKey] ?? "—"), value: Number(i[valueKey] ?? 0) }))
    .filter((i) => i.value > 0)
    .sort((a, b) => b.value - a.value);

  const top = sorted.slice(0, topN);
  const rest = sorted.slice(topN);
  const restSum = rest.reduce((s, i) => s + i.value, 0);
  const tree = rest.length
    ? [...top, { label: "Outros", value: restAggregate === "avg" ? restSum / rest.length : restSum }]
    : top;

  const titleNode = (
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <span>{title}</span>
      {info && (
        <div style={{ position: "relative" }}>
          <img
            src={InfoIcon}
            alt="Informação"
            onClick={() => setShowPopover(!showPopover)}
            style={{ width: "14px", height: "14px", cursor: "pointer", verticalAlign: "middle", display: "inline-block" }}
          />
          {showPopover && (
            <div
              style={{
                position: "absolute", top: "50%", left: "100%", transform: "translate(0, -50%)",
                marginLeft: "8px", backgroundColor: "#fff", border: "1px solid #ccc", padding: "10px",
                zIndex: 100, width: "240px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)", borderRadius: "4px"
              }}
            >
              <p style={{ margin: 0, fontSize: "12px", fontWeight: "normal" }}>{info}</p>
              <button
                onClick={() => setShowPopover(false)}
                style={{ marginTop: "8px", fontSize: "10px", cursor: "pointer", background: "none", border: "none", color: "#0962AD" }}
              >
                Fechar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (!tree.length) {
    return (
      <FinanceCardChart code={code} title={titleNode} height={height}>
        <ZeroData>
          <h4>Sem dados suficientes para o período.</h4>
        </ZeroData>
      </FinanceCardChart>
    );
  }

  const data = {
    datasets: [
      {
        tree,
        key: "value",
        borderWidth: 0,
        spacing: 1,
        backgroundColor: (ctx) => PALETTE[(ctx.dataIndex ?? 0) % PALETTE.length],
        labels: {
          display: true,
          color: "#fff",
          font: { size: 11 },
          overflow: "hidden",
          formatter: (ctx) => {
            const d = ctx.raw?._data ?? {};
            return [d.label, formatValue ? formatValue(d.value) : `${d.value}`];
          }
        }
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (evt, elements) => {
      const label = elements?.[0]?.element?.$context?.raw?._data?.label;
      selectBlock(label);
    },
    onHover: (evt, elements) => {
      if (evt?.native?.target) {
        const label = elements?.[0]?.element?.$context?.raw?._data?.label;
        evt.native.target.style.cursor = filterField && label ? "pointer" : "default";
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (arr) => arr?.[0]?.raw?._data?.label ?? "",
          label: (item) => {
            const v = item.raw?._data?.value ?? 0;
            return formatValue ? formatValue(v) : `${v} alunos`;
          }
        }
      }
    }
  };

  return (
    <FinanceCardChart code={code} title={titleNode} height={height}>
      <div style={{ height: "calc(100% - 40px)" }}>
        <Chart type="treemap" data={data} options={options} />
      </div>
    </FinanceCardChart>
  );
};

export default StudentsTreemap;
